import { ProductCapabilities } from '@awork/_shared/services/product/product-capabilities.service'
import type { Observable } from 'rxjs'
import { forkJoin, map } from 'rxjs'
import { Injectable, inject } from '@angular/core'
import type { ActivatedRouteSnapshot, RouterStateSnapshot, Route, NavigationExtras } from '@angular/router'
import { Router } from '@angular/router'
import { AccountService } from '@awork/_shared/services/account-service/account.service'
import { WorkspaceService } from '@awork/features/workspace/services/workspace-service/workspace.service'
import { UserService } from '@awork/features/user/services/user-service/user.service'
import { BrowserService } from '@awork/_shared/services/browser-service/browser.service'
import { apiProtocol, clientSecret } from '@awork/environments/environment'
import { decrypt } from '@awork/_shared/functions/encrypt'
import { queryStringToObject } from '@awork/_shared/functions/query-params-helper'
import { WorkspaceQuery } from '@awork/features/workspace/state/workspace.query'
import { Account } from '@awork/_shared/models/account.model'
import { AppStore } from '@awork/core/state/app.store'
import { AppQuery } from '@awork/core/state/app.query'
import { authentication, initialize } from '@microsoft/teams-js'
import getHostname from '@awork/_shared/functions/get-hostname'
import { SignalStoreService } from '@awork/core/state/signal-store/signalStore.service'
import { environment } from '@awork/environments/environment'

@Injectable({ providedIn: 'root' })
export class AuthGuard {
  private readonly product = inject(ProductCapabilities)
  private router = inject(Router)
  private appQuery = inject(AppQuery)
  private appStore = inject(AppStore)
  private accountService = inject(AccountService)
  private workspaceService = inject(WorkspaceService)
  private userService = inject(UserService)
  private workspaceQuery = inject(WorkspaceQuery)
  private browserService = inject(BrowserService)
  private signalStoreService = inject(SignalStoreService)

  refreshToken: string
  accessToken: string
  subdomain: string
  email: string
  expiresIn: number
  redirect: URL
  originalRedirectPath: string
  currentLoginRoute = '/login'

  // Email-related urls
  emailUrls = ['reset-password', 'accept-invitation', 'verify-email']

  currentUrl: string
  private queryParams: object

  private isTeams: boolean

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.signalStoreService.selectOnStorageInit().pipe(
      map(() => {
        const url = state.url

        this.currentUrl = url
        this.queryParams = route.queryParams

        // Determinate if the route comes from AuthComponent
        const fromAuth = route.parent?.data?.module === 'auth'

        if (route.queryParams['ms-teams'] || this.appQuery.getIsInMSTeams()) {
          this.currentLoginRoute = '/msteamslogin'
          this.isTeams = true
        }

        if (route.queryParams['d']) {
          // Decrypt object with email, token expiration date and redirection url
          const decryptedParams = JSON.parse(decrypt(clientSecret, decodeURIComponent(route.queryParams['d'])))

          this.email = decryptedParams.email
          this.expiresIn = decryptedParams.expiresIn

          // Store the original redirect path to preserve parentheses in auxiliary routes
          this.originalRedirectPath = decryptedParams.redirect

          try {
            this.redirect = new URL(decryptedParams.redirect, `${apiProtocol}${location.host}`)
          } catch (_) {}

          if (
            route.queryParams['access_token'] &&
            route.queryParams['subdomain'] &&
            route.queryParams['refresh_token']
          ) {
            this.refreshToken = route.queryParams['refresh_token']
            this.accessToken = route.queryParams['access_token']
            this.subdomain = route.queryParams['subdomain']
          }

          return this.checkLogin(url, true, true)
        }

        return this.checkLogin(url, fromAuth)
      })
    )
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.canActivate(route, state)
  }

  canLoad(route: Route): Observable<boolean> {
    return this.signalStoreService.selectOnStorageInit().pipe(
      map(() => {
        const url = `/${route.path}`
        return this.checkLogin(url)
      })
    )
  }

  /**
   * Checks if the user is logged in and redirect if needed
   * @param {string} url Referrer url, where the user was
   * @param {boolean} [fromAuth=false] If is from the Auth module or not
   * @param {boolean} [switchWorkspace=false] If it comes from a redirection to switch workspaces
   * @returns {boolean} If can navigate
   */
  checkLogin(url: string, fromAuth = false, switchWorkspace = false): boolean {
    const isLoggedIn = this.appQuery.getIsLoggedIn() || switchWorkspace

    if (isLoggedIn) {
      if (!fromAuth || this.emailUrls.some(emailUrl => this.currentUrl.includes(emailUrl))) {
        // If is logged in and is not from Auth module
        return true
      }

      if (fromAuth) {
        // If is logged in and is from Auth module
        if (this.workspaceQuery.getActive() || switchWorkspace) {
          if (switchWorkspace) {
            this.setLocalStorage()
          } else {
            // Navigate to the dashboard page if already logged in
            this.goToFramework()
          }

          return false
        } else {
          return true
        }
      }
    } else if (!isLoggedIn && fromAuth) {
      // If is not logged in and is from Auth module
      this.checkSignupDomain(url)

      // Exclude the subdomain cookie check from email-related urls
      if (!this.emailUrls.some(emailUrl => this.currentUrl.includes(emailUrl))) {
        // If the subdomain cookie is set, redirect to the workspace's subdomain
        // Preserve the current path (e.g., /login-email) during the redirect
        this.accountService.checkSubdomainCookie(true, location.pathname, this.queryParams)
      }

      // If the user is not logged in and in a subdomain, remove the subdomain cookie
      if (this.browserService.isCustomSubdomain()) {
        this.accountService.removeSubdomainCookie()
      }

      return true
    }

    let extras: NavigationExtras

    // If true, then the user just logged in or switch workspaces
    if (this.expiresIn && this.email) {
      extras = {
        queryParams: {
          loading: true,
          ...queryStringToObject(location.search)
        },
        queryParamsHandling: 'merge'
      }
    }

    // Navigate to the login page if not logged in and not from Auth module
    this.router.navigate(
      [this.currentLoginRoute],
      extras ? extras : { queryParams: queryStringToObject(location.search) }
    )

    return false
  }

  /**
   * Set all the localStorage with the new cookie
   */
  setLocalStorage(): void {
    const subdomain = this.browserService.getSubdomain()
    const account = new Account(this.email, this.expiresIn)

    this.accountService.updateAccount({
      email: this.email,
      refreshToken: this.refreshToken,
      accessToken: this.accessToken,
      refreshAt: account.refreshAt
    })

    // Sets user and workspace
    forkJoin([
      this.userService.fetchUser(),
      // If the subdomain is set in the URL, use it, otherwise use the one passed in the query string.
      this.workspaceService.getWorkspace(subdomain !== null ? subdomain : this.subdomain),
      this.workspaceService.getWorkspacesByEmail(this.email, {
        setActiveWorkspace: environment === 'local',
        syncStore: true
      })
    ]).subscribe(
      res => {
        this.appStore.update({ isLoggedIn: true })
        this.goToFramework()

        return false
      },
      err => {
        this.appStore.update({ isLoggedIn: false })
        this.router.navigate([this.currentLoginRoute])
        // TODO: error response for the user missing
      }
    )
  }

  /**
   * Redirects to inside the framework
   */
  private goToFramework(): void {
    let redirectUrl = this.accountService.appsFlyerRedirect(location.href)

    if (redirectUrl) {
      window.location.replace(redirectUrl)
    } else {
      redirectUrl = this.determineRedirectUrl()
    }

    // In case we are logged in and in ms teams, tell the frame
    // that the auth was successful.
    // This will close the login popup, otherwise you see awork in a
    // popup instead of a ms teams tab.
    if (redirectUrl === this.product.homeRoute && this.isTeams) {
      try {
        initialize()
        forkJoin([
          this.signalStoreService.selectOnStorePersisted('account'),
          this.signalStoreService.selectOnStorePersisted('app')
        ]).subscribe(() => authentication.notifySuccess(''))
      } catch (err) {}
    }

    const params = this.extractQueryParams()

    this.router.navigate([redirectUrl], { queryParams: params })
  }

  /**
   * Determines the redirect URL, prioritizing the original path to preserve auxiliary routes
   * @returns {string} The redirect URL path without query parameters
   */
  private determineRedirectUrl(): string {
    // Use the original redirect path to preserve parentheses in auxiliary routes
    // Note: We cannot use URL object here as it automatically encodes parentheses,
    // which breaks Angular's auxiliary route syntax (e.g., /path/(outlet:id))
    if (this.originalRedirectPath && !this.originalRedirectPath.includes('undefined')) {
      // Extract the path part only (before any query string)
      const pathMatch = this.originalRedirectPath.match(/^([^?#]+)/)

      return pathMatch ? pathMatch[1] : this.product.homeRoute
    }

    // Fallback to redirect URL object
    if (this.redirect && this.redirect.pathname && !this.redirect.pathname.includes('undefined')) {
      return this.redirect.pathname
    }

    return this.product.homeRoute
  }

  /**
   * Extracts query parameters from the redirect path or URL object
   * @returns {object} Query parameters as key-value pairs
   */
  private extractQueryParams(): object {
    if (this.originalRedirectPath) {
      // Extract query string from the original path
      const queryMatch = this.originalRedirectPath.match(/\?(.+)/)

      return queryStringToObject(queryMatch ? queryMatch[1] : location.search)
    }

    return queryStringToObject(this.redirect ? this.redirect.search : location.search)
  }

  /**
   * If the user goes to the sign-up page in a custom subdomain, redirects to the global sign-up page
   * @param {string} url
   */
  private checkSignupDomain(url: string): void {
    if (url === '/signup' && this.browserService.isCustomSubdomain()) {
      const hostname = getHostname()

      window.location.replace(`https://app.${hostname}/signup`)
    }
  }
}
